import { updateApiMutation } from "@/api/api";
import ApiManagementSectionHeader from "@/components/api-management/ApiManagementSectionHeader.component";
import FormEnumList from "@/components/forms/FormEnumList.component";
import { IEnumNameBlurEvent } from "@/components/forms/FormEnumWidget.component";
import ObjectTypeList from "@/components/object-type/ObjectTypeList.component";
import { ObjectTypeNameValidator } from "@/components/object-type/ObjectTypeNameValidator.mixin";
import Tabs from "@/components/shared/Tabs.component";
import { BButton, BContainer, BRow, BSpinner } from "@/lib/typed-bootstrap";
import {
  declarationSchemaForInput,
  IApiState,
  IEnumState,
  IEnumValueState,
  IFieldState,
  IObjectTypeState,
} from "@/models";
import { GraphqlApiSchemaHandler } from "@/models/api-schema/graphql-api-schema-handler";
import { persistanceService } from "@/utils/persistance-service";
import { ApolloClient } from "apollo-boost";
import { IsEqualCustomizer } from "lodash";
import _cloneDeep from "lodash/cloneDeep";
import _removeFp from "lodash/fp/remove";
import _isEqualWith from "lodash/isEqualWith";
import { Component, Prop, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

@Component<ApiDataTypes>({
  beforeRouteLeave(to, from, next) {
    this.onSaveTypesChange();
    next();
  },
})
export default class ApiDataTypes extends ObjectTypeNameValidator {
  _tsx!: tsx.DeclareProps<{
    value: ApiDataTypes["value"];
    apiClient: ApiDataTypes["apiClient"];
    apiSchemaHandler: ApiDataTypes["apiSchemaHandler"];
  }> &
    tsx.DeclareOnEvents<{}>;

  @Prop({ required: true }) value: IApiState;
  @Prop({ required: true }) apiClient: ApolloClient<unknown>;
  @Prop({ required: true }) apiSchemaHandler: GraphqlApiSchemaHandler | null;

  isLoading = false;

  enumTypes: IEnumState[] = [];

  private currentTabIndex = 0;

  selectedAction = null;
  actionOptions = [
    { value: "delete", text: this.$t("global_cta_delete") },
    { value: "duplicate", text: this.$t("global_cta_duplicate") },
  ];

  equalWithCustomizer: IsEqualCustomizer = (j: any, k: any, key: any) => {
    return key === "selected" ? true : undefined;
  };

  get noTypeSelected(): boolean {
    return (
      this.objectTypes?.some((type) => {
        return type.selected;
      }) != true
    );
  }

  get objectListKey(): string {
    return `${this.enumTypes?.length}-${this.objectTypes?.length}`;
  }

  get selectedEnumType(): IEnumState | undefined {
    return this.enumTypes.find((type: IEnumState) => type.selected);
  }

  get selectedObjectType(): IObjectTypeState | undefined {
    return this.objectTypes.find((type: IObjectTypeState) => type.selected);
  }

  get showCancelSaveButton(): boolean {
    return (
      !_isEqualWith(
        this.objectTypes,
        this.value.types,
        this.equalWithCustomizer,
      ) ||
      !_isEqualWith(this.enumTypes, this.value.enums, this.equalWithCustomizer)
    );
  }

  get objectNameInHash() {
    return this.$route.hash.substring(1);
  }

  @Watch("value", { immediate: true })
  initialize() {
    this.objectTypes = _cloneDeep(this.value.types);
    this.enumTypes = _cloneDeep(this.value.enums);
    this.updateSelectedObjectType();
  }

  @Watch("objectNameInHash")
  updateSelectedObjectType(name: string = this.objectNameInHash) {
    const objectTypeIndex = this.objectTypes?.findIndex(
      (type) => type.name === name,
    );
    if (objectTypeIndex > 0) {
      this.objectTypes?.forEach((objectType, index) => {
        if (index === objectTypeIndex) {
          objectType.selected = true;
        } else {
          objectType.selected = false;
        }
      });
    }
  }

  updateHash() {
    if (this.objectNameInHash !== this.selectedObjectType?.name) {
      const fullPath = `${this.$route.path}#${this.selectedObjectType?.name}`;
      this.$router.push(fullPath);
      persistanceService.setActiveRouteKey(fullPath);
    }
  }

  resetSelectionIfNecessary() {
    const someTypeIsSelected = this.objectTypes.some((t) => t.selected);
    if (!someTypeIsSelected) {
      this.objectTypes[0] ? (this.objectTypes[0].selected = true) : null;
    }

    const isSomeEnumTypeSelected = this.enumTypes.some((t) => t.selected);
    if (!isSomeEnumTypeSelected) {
      this.enumTypes[0] ? (this.enumTypes[0].selected = true) : null;
    }
  }

  removeUnusedEnumEntries(enums: IEnumState[]) {
    enums = _removeFp((type: IEnumState) => {
      return type.name === "" && type.values.length === 0;
    }, enums);

    return enums.map((type) => {
      type.name = type.name.replace(/\s+/g, "");
      type.values = _removeFp((value: IEnumValueState) => {
        return value.name === null || value.name === "";
      }, type.values);

      return type;
    });
  }

  removeUnusedTypeEntries(types: IObjectTypeState[]): IObjectTypeState[] {
    types = _removeFp((type: IObjectTypeState) => {
      return (
        type.name === "" &&
        type.targets.length === 0 &&
        type.fields.length <= 3 &&
        !type.parents.first?.parentId &&
        !type.parents.second?.parentId
      );
    }, types);

    return types.map((type) => {
      // remove unset targets and fields
      type.name = type.name.replace(/\s+/g, "");
      type.targets = _removeFp({ targetId: null }, type.targets);
      type.fields = _removeFp((field: IFieldState) => {
        return field.type === null || field.name === "";
      }, type.fields);

      return type;
    });
  }

  onEnumNameChange({ oldName, newName }: IEnumNameBlurEvent) {
    if (oldName === "") return;

    this.objectTypes.forEach((type) => {
      type.fields.forEach((field) => {
        if (field.type === oldName) {
          field.type = newName;
        }
      });
    });
  }

  onCancelTypesChange() {
    this.objectTypes = _cloneDeep(this.value.types);
    this.enumTypes = _cloneDeep(this.value.enums);
  }

  async onSaveTypesChange(): Promise<void> {
    if (this.errorMessage) {
      return;
    }

    try {
      this.isLoading = true;
      this.enumTypes = this.removeUnusedEnumEntries(this.enumTypes);
      this.value.enums = _cloneDeep(this.enumTypes);
      this.objectTypes = this.removeUnusedTypeEntries(this.objectTypes);
      this.value.types = _cloneDeep(this.objectTypes);
      this.resetSelectionIfNecessary();
      await this.createApiDeclarationFromData(this.value);
    } catch (err) {
      console.log(err);
    } finally {
      this.isLoading = false;
    }
  }

  private onTabIndexChange(index: number) {
    this.currentTabIndex = index;
  }

  async createApiDeclarationFromData(apiData: IApiState): Promise<void> {
    try {
      apiData.schema = declarationSchemaForInput(apiData);
      await updateApiMutation({ id: apiData.id, schema: apiData.schema });
    } catch (err) {
      console.log(err);
    }
  }

  onSelectObjectType(objectType: IObjectTypeState) {
    this.updateSelectedObjectType(objectType.name);
    this.updateHash();
  }

  render() {
    return (
      <BContainer class="api__view-setup" fluid>
        <ApiManagementSectionHeader
          title={this.$t("api_details_section_title_model").toString()}
          description={this.$t(
            "api_details_section_description_model",
          ).toString()}
        />
        <Tabs
          class="datamodel-tabs"
          currentTabIndex={this.currentTabIndex}
          onTabIndexChange={this.onTabIndexChange}
        >
          <Tabs.Tab title="Types" id="types">
            <ObjectTypeList
              class="mt-2"
              value={this.objectTypes}
              apiAuthMode={this.value.defaultAuthMode ?? undefined}
              allEnumTypes={this.enumTypes}
              key={`object-list-${this.objectListKey}`}
              errorMessage={this.errorMessage}
              objectTypeWithDuplicate={this.objectTypeWithDuplicate}
              objectTypeWithReservedType={this.objectTypeWithReservedType}
              apiClient={this.apiClient}
              apiSchemaHandler={this.apiSchemaHandler}
              apiState={this.value}
              onSelectObjectType={this.onSelectObjectType}
            />
          </Tabs.Tab>
          <Tabs.Tab title="Enums" id="enums">
            <FormEnumList
              class="mt-2"
              value={this.enumTypes}
              onEnumNameChange={this.onEnumNameChange}
            />
          </Tabs.Tab>
        </Tabs>
        {this.showCancelSaveButton && (
          <BRow class="buttons__section">
            <BButton
              class="action-button__pill--outline action-button__flex-props"
              onClick={(e) => {
                e.preventDefault();
                this.onCancelTypesChange();
              }}
              type="reset"
            >
              {this.$t("global_cta_cancel")}
            </BButton>
            <BButton
              class="action-button__pill--gradient action-button__flex-props"
              onClick={(e) => {
                e.preventDefault();
                this.onSaveTypesChange();
              }}
              type="submit"
              disabled={!!this.errorMessage}
            >
              {this.isLoading && (
                <div class="default__spinner--small" role="status">
                  <BSpinner
                    class="ml-auto"
                    type="grow"
                    label="Saving..."
                    small
                  />
                </div>
              )}
              {this.$t("global_cta_save")}
            </BButton>
          </BRow>
        )}
      </BContainer>
    );
  }
}
